import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import '../styles/HomePage.css';
import { useAuth } from '../context/AuthContext'; // ✅ Import AuthContext

export default function HomePage() {
  const [statesData, setStatesData] = useState([]);
  const [placeIndexes, setPlaceIndexes] = useState({});
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const { fetchWithAuth, user } = useAuth(); // ✅ Use auth fetch
  const API_URL = process.env.REACT_APP_API_URL; // "http://localhost:5000"

  useEffect(() => {
    checkAuthStatus();
    fetchPlaces();
  }, []);

  // ------------------ Auth check ------------------
  const checkAuthStatus = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/check-auth`, { method: 'GET' });
      const data = await response.json();
      setIsLoggedIn(data.loggedIn);
    } catch (err) {
      console.error('Auth check error:', err);
      setIsLoggedIn(false);
    }
  };

  // ------------------ Fetch places ------------------
  const fetchPlaces = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/get-places`, { method: 'GET' });
      if (!response.ok) throw new Error('Failed to fetch places');

      const data = await response.json();

      // Group places by state
      const grouped = data.places.reduce((acc, place) => {
        if (!acc[place.state]) acc[place.state] = [];
        acc[place.state].push(place);
        return acc;
      }, {});

      // Convert object to array
      const groupedArray = Object.keys(grouped).map(state => ({
        state,
        places: grouped[state],
      }));

      // Initialize each state's place index to 0
      const indexes = {};
      groupedArray.forEach(s => {
        indexes[s.state] = 0;
      });

      setStatesData(groupedArray);
      setPlaceIndexes(indexes);
    } catch (err) {
      console.error('Error fetching places:', err);
      setError('Failed to load places. Please try again later.');
    }
  };

  const handleNextPlace = (state) => {
    setPlaceIndexes(prev => ({
      ...prev,
      [state]: (prev[state] + 1) % statesData.find(s => s.state === state).places.length
    }));
  };

  const handlePrevPlace = (state) => {
    setPlaceIndexes(prev => ({
      ...prev,
      [state]: (prev[state] - 1 + statesData.find(s => s.state === state).places.length) %
        statesData.find(s => s.state === state).places.length
    }));
  };

  const handleGetStarted = () => {
    if (!localStorage.getItem('loggedOut')) {
      if (user.role === "guide") {
        navigate('/guide-dashboard');
      } else {
        navigate('/dashboard');
      }

    } else {
      navigate('/login');
    }
  };

  return (
    <div className='home-container'>
      <Navbar />

      {/* Enhanced Hero Section */}
      <div className="home-hero">
        <div className="hero-background">
          <img
            src="https://images.unsplash.com/photo-1614088459293-5669fadc3448?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHx0cmF2ZWwlMjBkZXN0aW5hdGlvbnxlbnwwfHx8fDE3NTY3MjM1MTB8MA&ixlib=rb-4.1.0&q=85"
            alt="Travel Hero"
            className="hero-image"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            Welcome to SoulfulYatra
          </h1>


          <p>Your all-in-one travel companion for exploring the world's most soulful places</p>
          <div className="hero-features">
            <div className="feature-item">
              <span className="feature-icon">🗺️</span>
              <span>Explore Places</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">✈️</span>
              <span>Plan Trips</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🧭</span>
              <span>Hire a Guide</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🎥</span>
              <span>Travel Vlogs</span>
            </div>
          </div>
          <button className="cta-button" onClick={handleGetStarted}>
            {!localStorage.getItem('loggedOut') ? 'Go to Dashboard' : 'Get Started'}
          </button>
        </div>
      </div>

      <div className='home-content'>
        {/* Quick Actions Section */}
        <section className="quick-actions-section">
          <div className="container">
            <h2>Start Your Journey</h2>
            <div className="quick-actions-grid">
              <div className="action-card" onClick={() => navigate('/explore-places')}>
                <div className="action-icon bg-blue">🗺️</div>
                <h3>Explore Places</h3>
                <p>Discover amazing destinations around the world</p>
              </div>
              <div className="action-card" onClick={() => navigate('/plan-trip')}>
                <div className="action-icon bg-green">✈️</div>
                <h3>Plan Trip</h3>
                <p>Create personalized itineraries with AI assistance</p>
              </div>
              <div className="action-card" onClick={() => navigate('/book-guide')}>
                <div className="action-icon bg-purple">🧭</div>
                <h3>Hire a Guide</h3>
                <p>Hire local guides for your trip</p>
              </div>
              <div className="action-card" onClick={() => navigate('/vlogs')}>
                <div className="action-icon bg-orange">🎥</div>
                <h3>Travel Vlogs</h3>
                <p>Watch exciting travel videos</p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Destinations */}
        {error && <p className='error-message'>{error}</p>}

        {statesData.length > 0 ? (
          <section className="featured-destinations">
            <div className="container">
              <h2>Featured Destinations</h2>
              <div className="destinations-grid">
                {statesData.slice(0, 6).map((stateObj) => {
                  const place = stateObj.places[placeIndexes[stateObj.state]];
                  return (
                    <div key={stateObj.state} className="destination-card">
                      <div className="destination-image">
                        <img src={place.image_url} alt={place.name} />
                        <div className="destination-overlay">
                          <div className="next-buttons">
                            <button
                              className="next-btn"
                              onClick={() => handlePrevPlace(stateObj.state)}
                            >
                              ‹
                            </button>
                            <button
                              className="next-btn"
                              onClick={() => handleNextPlace(stateObj.state)}
                            >
                              ›
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="destination-content">
                        <div className="destination-header">
                          <h3>{place.place_name}</h3>
                          <span className="destination-state">{stateObj.state}</span>
                        </div>
                        <p className="destination-description">{place.description}</p>
                        <button
                          className="home-explore-btn"
                          onClick={() => navigate('/explore-places')}
                        >
                          Explore More
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        ) : (
          <div className="loading-destinations">
            <div className="loading-spinner"></div>
            <p>Loading amazing destinations...</p>
          </div>
        )}

        {/* Features Section */}
        <section className="features-section">
          <div className="container">
            <h2>Why Choose SoulfulYatra?</h2>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-image">
                  <img
                    src="https://images.unsplash.com/photo-1584467541268-b040f83be3fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHx0cmF2ZWwlMjBkZXN0aW5hdGlvbnxlbnwwfHx8fDE3NTY3MjM1MTB8MA&ixlib=rb-4.1.0&q=85"
                    alt="AI Planning"
                  />
                </div>
                <div className="feature-content">
                  <h3>AI-Powered Planning</h3>
                  <p>Get personalized itineraries created by advanced AI technology tailored to your preferences and interests.</p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-image">
                  <img
                    src="https://images.unsplash.com/photo-1525849306000-cc26ceb5c1d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHx0cmF2ZWwlMjBkZXN0aW5hdGlvbnxlbnwwfHx8fDE3NTY3MjM1MTB8MA&ixlib=rb-4.1.0&q=85"
                    alt="All in One"
                  />
                </div>
                <div className="feature-content">
                  <h3>All-in-One Platform</h3>
                  <p>Plan trips, book hotels, find restaurants, and arrange transportation all in one convenient place.</p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-image">
                  <img
                    src="https://images.unsplash.com/photo-1682685797857-97de838c192e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHx0cmF2ZWwlMjBkZXN0aW5hdGlvbnxlbnwwfHx8fDE3NTY3MjM1MTB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Personalized"
                  />
                </div>
                <div className="feature-content">
                  <h3>Personalized Experience</h3>
                  <p>Every recommendation is tailored to your travel style, budget, and preferences for the perfect journey.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Start Your Adventure?</h2>
              <p>Join thousands of travelers who trust SoulfulYatra for their perfect journeys</p>
              <div className="cta-buttons">
                <button
                  className="primary-cta"
                  onClick={handleGetStarted}
                >
                  {isLoggedIn ? 'Go to Dashboard' : 'Sign Up Now'}
                </button>
                <button
                  className="secondary-cta"
                  onClick={() => navigate('/explore-places')}
                >
                  Explore Places
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}